import React from 'react';
import { NavLink } from 'react-router-dom';
import { GiHiveMind } from 'react-icons/gi';
import { FaCloud, FaArrowRight, FaRobot, FaBolt, FaShieldAlt } from 'react-icons/fa';
import { MdOutlineIntegrationInstructions } from 'react-icons/md';
import { HiOutlineLightBulb, HiOutlineChartBar, HiOutlineCog, HiOutlineSparkles } from 'react-icons/hi';
import './Homepage.css';
import img1 from '../Components/Assets/About1.jpeg';

export const Homepage = () => {
  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content container">
          <div className="hero-text">
            <span className="badge">Software Services & Consulting</span>
            <h1>Digitally Transformed Human Experiences</h1>
            <p>
              Critical decisions are comparable to a one-way door — they cannot be
              reversed once made. We are your trusted partner in software services and
              consulting, helping your business navigate the digital landscape with confidence.
            </p>
            <div className="hero-actions">
              <NavLink to="/contact" className="btn btn-primary">
                Get Started <FaArrowRight />
              </NavLink>
              <NavLink to="/about" className="btn btn-outline">
                Learn More
              </NavLink>
            </div>
          </div>
          <div className="hero-image">
            <img src={img1} alt="Digital transformation" />
          </div>
        </div>
      </section>

      {/* AI-Powered Section */}
      <section className="section ai-hero-section">
        <div className="container">
          <div className="section-header">
            <span className="badge">AI-Powered</span>
            <h2>Build Smarter, Ship Faster with AI</h2>
            <p>
              We harness artificial intelligence to deliver software solutions 10X faster
              — more reliable, more intelligent, and built for the future.
            </p>
          </div>

          <div className="ai-highlight-grid">
            <div className="ai-highlight-card ai-highlight-main">
              <div className="ai-highlight-icon"><FaBolt /></div>
              <div className="ai-highlight-number">10X</div>
              <h3>Faster Delivery</h3>
              <p>AI-accelerated development across the full SDLC — from code generation to automated testing — dramatically cuts time-to-market.</p>
            </div>
            <div className="ai-highlight-card">
              <div className="ai-highlight-icon"><FaShieldAlt /></div>
              <div className="ai-highlight-number">70%</div>
              <h3>Fewer Defects</h3>
              <p>AI-powered code review and automated testing catches bugs early, reducing production incidents significantly.</p>
            </div>
            <div className="ai-highlight-card">
              <div className="ai-highlight-icon"><FaRobot /></div>
              <div className="ai-highlight-number">24/7</div>
              <h3>AI Agents</h3>
              <p>Intelligent agents automate day-to-day tasks — from monitoring to code generation — so your team focuses on high-value work.</p>
            </div>
          </div>

          <div className="ai-bedrock-bar">
            <div className="ai-bedrock-label">
              <HiOutlineSparkles /> Powered by AWS Bedrock
            </div>
            <div className="ai-model-scroll">
              <span className="model-chip">Anthropic Claude 4</span>
              <span className="model-chip">Claude Sonnet 4</span>
              <span className="model-chip">Amazon Nova Pro</span>
              <span className="model-chip">Amazon Nova Premier</span>
              <span className="model-chip">Meta Llama 4</span>
              <span className="model-chip">Mistral Large</span>
              <span className="model-chip">Cohere Command R+</span>
            </div>
          </div>

          <div className="ai-cta-row">
            <NavLink to="/about" className="btn btn-primary">
              Explore AI Solutions <FaArrowRight />
            </NavLink>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="section section-alt">
        <div className="container">
          <div className="section-header">
            <span className="badge">What We Do</span>
            <h2>Our Core Services</h2>
            <p>
              We deliver solutions that redefine industry standards through expert 
              consulting, modern engineering, and strategic thinking.
            </p>
          </div>
          <div className="grid-3">
            <div className="card service-card">
              <div className="service-icon">
                <GiHiveMind />
              </div>
              <h3>Artificial Intelligence</h3>
              <p>Leverage AI and machine learning to automate processes and gain insights that drive smarter business decisions.</p>
            </div>
            <div className="card service-card">
              <div className="service-icon">
                <FaCloud />
              </div>
              <h3>Cloud & DevOps</h3>
              <p>Migrate to the cloud and streamline your development pipeline with modern DevOps practices for faster delivery.</p>
            </div>
            <div className="card service-card">
              <div className="service-icon">
                <MdOutlineIntegrationInstructions />
              </div>
              <h3>Software Development</h3>
              <p>Build durable, scalable software solutions tailored to your business needs using modern technologies.</p>
            </div>
          </div>
          <div className="section-cta">
            <NavLink to="/service" className="btn btn-outline">
              View All Services <FaArrowRight />
            </NavLink>
          </div>
        </div>
      </section>

      {/* How We Deliver Value */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="badge">Our Approach</span>
            <h2>How We Deliver Value</h2>
            <p>
              From strategy to execution, our multi-disciplinary approach ensures every solution
              is designed for impact and built for scale.
            </p>
          </div>
          <div className="grid-4 value-grid">
            <div className="value-card">
              <div className="value-icon-wrap">
                <HiOutlineLightBulb />
              </div>
              <h3>Design</h3>
              <p>Crafting exceptional digital experiences using design thinking that puts your users first and drives engagement.</p>
            </div>
            <div className="value-card">
              <div className="value-icon-wrap">
                <HiOutlineChartBar />
              </div>
              <h3>Strategy</h3>
              <p>Defining your digital transformation roadmap with data-driven insights and clear, measurable outcomes.</p>
            </div>
            <div className="value-card">
              <div className="value-icon-wrap">
                <HiOutlineCog />
              </div>
              <h3>Engineering</h3>
              <p>Building durable, scalable software across modern platforms — secure, flexible, and built to last.</p>
            </div>
            <div className="value-card value-card-ai">
              <div className="value-icon-wrap value-icon-ai">
                <HiOutlineSparkles />
              </div>
              <h3>AI & Automation</h3>
              <p>Deploying intelligent agents and AI-driven workflows that eliminate repetitive work and amplify human potential.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Clients Banner */}
      <section className="section section-alt clients-banner">
        <div className="container">
          <div className="section-header">
            <span className="badge">Trusted By</span>
            <h2>Partnering with Industry Leaders</h2>
          </div>
          <div className="home-clients-row">
            <div className="home-client-item">
              <span className="home-client-name">Deloitte</span>
            </div>
            <div className="home-client-divider"></div>
            <div className="home-client-item">
              <span className="home-client-name">HP / Mphasis</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>Partner with us to unlock your full potential and achieve your digital ambitions.</p>
            <NavLink to="/contact" className="btn btn-white">
              Schedule a Consultation <FaArrowRight />
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
};
